import React, { useState, useEffect } from 'react';
import { 
  Wallet, Calendar, CreditCard, CheckCircle, Clock, AlertTriangle,
  ExternalLink, History, Loader2, Building2, FileText, ChevronDown, ChevronUp,
  Upload, X, Receipt
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PAYMENT_METHODS = {
  transfer: { label: 'Transferencia', icon: '🏦' },
  spei: { label: 'SPEI', icon: '🏦' },
  paypal: { label: 'PayPal', icon: '💳' },
  mercadopago: { label: 'Mercado Pago', icon: '💳' },
  efectivo: { label: 'Efectivo', icon: '💵' }
};

export default function MyAccountTab() {
  const [loading, setLoading] = useState(true);
  const [businesses, setBusinesses] = useState([]);
  const [expanded, setExpanded] = useState(null);
  
  // New state for scheduled payments
  const [paymentsData, setPaymentsData] = useState(null);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(null);

  useEffect(() => {
    loadAccount();
    loadMyPayments();
  }, []);

  const loadAccount = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('portal_token');
      const response = await axios.get(`${API_URL}/api/billing/my-account`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBusinesses(response.data.businesses || []);
      
      // Auto-expand first business
      if (response.data.businesses?.length === 1) {
        setExpanded(response.data.businesses[0].business_id);
      }
    } catch (error) {
      console.error('Error loading account:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMyPayments = async () => {
    try {
      setLoadingPayments(true);
      const token = localStorage.getItem('portal_token');
      const response = await axios.get(`${API_URL}/api/billing/portal/my-payments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPaymentsData(response.data);
    } catch (error) {
      console.error('Error loading payments:', error);
    } finally {
      setLoadingPayments(false);
    }
  };

  const getStatusBadge = (biz) => {
    const status = biz.payment_status;
    const nextDate = biz.next_billing_date;
    
    if (status === 'paid') {
      return (
        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-500/20 text-green-400 rounded-full text-sm font-medium">
          <CheckCircle className="w-4 h-4" />
          Al día
        </span>
      );
    }
    
    if (nextDate) {
      const today = new Date();
      const dueDate = new Date(nextDate + 'T00:00:00');
      const diff = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));
      
      if (diff < 0) {
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-500/20 text-red-400 rounded-full text-sm font-medium">
            <AlertTriangle className="w-4 h-4" />
            Pago pendiente
          </span>
        );
      } else if (diff <= 5) {
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-orange-500/20 text-orange-400 rounded-full text-sm font-medium">
            <Clock className="w-4 h-4" />
            {diff === 0 ? 'Vence hoy' : `Vence en ${diff} días`}
          </span>
        );
      }
    }
    
    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[rgba(232,106,31,0.15)] text-[#E86A1F] rounded-full text-sm font-medium">
        <Clock className="w-4 h-4" />
        Próximo pago
      </span>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('es-MX', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatShortDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('es-MX', {
      day: 'numeric',
      month: 'short'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="my-account-tab">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-3 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl">
          <Wallet className="w-6 h-6 text-[#1D1D1F]" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-[#1D1D1F]">Mi Cuenta</h2>
          <p className="text-sm text-[#AEAEB2]">Estado de pagos y facturación</p>
        </div>
      </div>

      {/* ============ SECCIÓN: MIS PAGOS ============ */}
      <div className="bg-white/50 rounded-xl border border-[rgba(0,0,0,0.06)]/50 p-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-gradient-to-br from-amber-500/20 to-orange-500/20 rounded-lg">
            <Receipt className="w-5 h-5 text-[#E86A1F]" />
          </div>
          <h3 className="text-lg font-semibold text-[#1D1D1F]">Mis Pagos</h3>
        </div>

        {loadingPayments ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 text-orange-500 animate-spin" />
          </div>
        ) : paymentsData?.current_payment ? (
          <div className="space-y-4">
            {/* Pago Actual */}
            <div className="bg-white/40 rounded-xl p-4 border border-[rgba(0,0,0,0.06)]/50">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm text-[#AEAEB2]">Pago actual</span>
                {paymentsData.current_payment.is_overdue ? (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-red-500/20 text-red-400 rounded-full text-xs font-medium">
                    <AlertTriangle className="w-3.5 h-3.5" />
                    Vencido
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-[rgba(232,106,31,0.15)] text-[#E86A1F] rounded-full text-xs font-medium">
                    <Clock className="w-3.5 h-3.5" />
                    Pendiente
                  </span>
                )}
              </div>
              
              <div className="space-y-3">
                <div>
                  <p className="text-[#1D1D1F] font-medium">{paymentsData.current_payment.service_name || paymentsData.current_payment.description || 'Servicio mensual'}</p>
                  {paymentsData.current_payment.business_name && (
                    <p className="text-sm text-[#AEAEB2]">{paymentsData.current_payment.business_name}</p>
                  )}
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold text-[#1D1D1F]">
                      ${paymentsData.current_payment.amount?.toLocaleString()} <span className="text-sm text-[#AEAEB2] font-normal">MXN</span>
                    </p>
                    <p className="text-sm text-[#AEAEB2]">
                      Vence: {formatDate(paymentsData.current_payment.due_date)}
                    </p>
                  </div>
                </div>
                
                {/* Botón subir comprobante */}
                {!paymentsData.current_payment.receipt_submitted ? (
                  <button
                    onClick={() => setShowUploadModal(paymentsData.current_payment)}
                    className="w-full mt-2 py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-xl font-semibold flex items-center justify-center gap-2 hover:shadow-lg transition"
                    data-testid="upload-receipt-btn"
                  >
                    <Upload className="w-5 h-5" />
                    Subir comprobante de pago
                  </button>
                ) : (
                  <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-3 flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                    <div>
                      <p className="text-green-400 font-medium text-sm">Comprobante enviado</p>
                      <p className="text-green-400/70 text-xs">Lo estamos revisando. Te confirmaremos pronto.</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Historial */}
            {paymentsData.history && paymentsData.history.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-[#AEAEB2] mb-3 flex items-center gap-2">
                  <History className="w-4 h-4" />
                  Historial de pagos
                </h4>
                <div className="bg-white/65/30 rounded-xl overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-[#AEAEB2] border-b border-[rgba(0,0,0,0.06)]/50">
                        <th className="px-4 py-2">Fecha</th>
                        <th className="px-4 py-2">Concepto</th>
                        <th className="px-4 py-2 text-right">Monto</th>
                        <th className="px-4 py-2 text-center">Estado</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[rgba(0,0,0,0.05)]/30">
                      {paymentsData.history.slice(0, 5).map((payment, idx) => (
                        <tr key={idx} className="hover:bg-white/70/30">
                          <td className="px-4 py-3 text-[#AEAEB2]">{formatShortDate(payment.paid_date)}</td>
                          <td className="px-4 py-3 text-[#1D1D1F]">{payment.service_name || payment.description || 'Servicio'}</td>
                          <td className="px-4 py-3 text-right text-[#1D1D1F] font-medium">${payment.amount?.toLocaleString()}</td>
                          <td className="px-4 py-3 text-center">
                            <CheckCircle className="w-4 h-4 text-green-500 inline" />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-6 text-center">
            <CheckCircle className="w-10 h-10 text-green-400 mx-auto mb-3" />
            <p className="text-green-400 font-medium">No tienes pagos pendientes por el momento. ✅</p>
          </div>
        )}
      </div>

      {/* ============ SECCIÓN: INFORMACIÓN DE CUENTA (legacy) ============ */}
      {businesses.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-[#AEAEB2] uppercase tracking-wider">Información de facturación</h3>
          
          {businesses.map((biz) => {
            const isExpanded = expanded === biz.business_id;
            const method = PAYMENT_METHODS[biz.payment_method] || PAYMENT_METHODS.transfer;
            const breakdown = biz.amount_breakdown || {};
            
            return (
              <div 
                key={biz.business_id} 
                className="bg-white/50 rounded-xl border border-[rgba(0,0,0,0.06)]/50 overflow-hidden"
              >
                {/* Header */}
                <button
                  onClick={() => setExpanded(isExpanded ? null : biz.business_id)}
                  className="w-full p-5 flex items-center justify-between hover:bg-white/70/80 transition"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-green-500/20 to-emerald-500/20 rounded-xl flex items-center justify-center">
                      <Building2 className="w-6 h-6 text-green-400" />
                    </div>
                    <div className="text-left">
                      <h3 className="font-semibold text-[#1D1D1F]">{biz.business_name}</h3>
                      <p className="text-sm text-[#AEAEB2]">
                        ${breakdown.total?.toLocaleString() || '0'} MXN / mes
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {getStatusBadge(biz)}
                    {isExpanded ? (
                      <ChevronUp className="w-5 h-5 text-[#AEAEB2]" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-[#AEAEB2]" />
                    )}
                  </div>
                </button>

                {/* Expanded Content */}
                {isExpanded && (
                  <div className="px-5 pb-5 space-y-4">
                    {/* Next Payment Date */}
                    <div className="bg-white/40 rounded-xl p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-[#AEAEB2] mb-1">Próxima fecha de pago</p>
                          <p className="text-lg font-semibold text-[#1D1D1F]">
                            {formatDate(biz.next_billing_date)}
                          </p>
                        </div>
                        <Calendar className="w-8 h-8 text-[#6E6E73]" />
                      </div>
                    </div>

                    {/* Amount Breakdown */}
                    <div className="bg-white/40 rounded-xl p-4">
                      <h4 className="text-sm font-medium text-[#AEAEB2] mb-3">Desglose del pago</h4>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-[#AEAEB2]">Servicio mensual</span>
                          <span className="text-[#1D1D1F]">${breakdown.subtotal?.toLocaleString() || '0'}</span>
                        </div>
                        {breakdown.iva_amount > 0 && (
                          <div className="flex justify-between text-sm">
                            <span className="text-[#AEAEB2]">IVA (16%)</span>
                            <span className="text-[#1D1D1F]">${breakdown.iva_amount?.toLocaleString()}</span>
                          </div>
                        )}
                        {breakdown.card_fee > 0 && (
                          <div className="flex justify-between text-sm">
                            <span className="text-[#AEAEB2]">Comisión por tarjeta (5%)</span>
                            <span className="text-[#1D1D1F]">${breakdown.card_fee?.toLocaleString()}</span>
                          </div>
                        )}
                        <div className="flex justify-between text-lg font-bold pt-2 border-t border-[rgba(0,0,0,0.06)]">
                          <span className="text-[#1D1D1F]">Total a pagar</span>
                          <span className="text-green-400">${breakdown.total?.toLocaleString() || '0'} MXN</span>
                        </div>
                      </div>
                    </div>

                    {/* Payment Method */}
                    <div className="bg-white/40 rounded-xl p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-[#AEAEB2] mb-1">Método de pago</p>
                          <p className="text-[#1D1D1F] flex items-center gap-2">
                            <span>{method.icon}</span>
                            {method.label}
                          </p>
                        </div>
                        <CreditCard className="w-6 h-6 text-[#6E6E73]" />
                      </div>
                    </div>

                    {/* Pay Now Button (for card payments) */}
                    {biz.can_pay_online && biz.payment_link && biz.payment_status !== 'paid' && (
                      <a
                        href={biz.payment_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block w-full py-4 bg-gradient-to-r from-green-500 to-emerald-500 text-white text-center rounded-xl font-bold hover:shadow-lg transition flex items-center justify-center gap-2"
                      >
                        <CreditCard className="w-5 h-5" />
                        Pagar Ahora
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    )}

                    {/* Payment History */}
                    {biz.payment_history && biz.payment_history.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-[#AEAEB2] mb-3 flex items-center gap-2">
                          <History className="w-4 h-4" />
                          Historial de pagos
                        </h4>
                        <div className="space-y-2">
                          {biz.payment_history.slice(0, 5).map((payment, idx) => (
                            <div 
                              key={idx} 
                              className="flex items-center justify-between bg-white/65/30 rounded-lg p-3"
                            >
                              <div>
                                <p className="text-[#1D1D1F] font-medium">
                                  ${payment.amount?.toLocaleString()} MXN
                                </p>
                                <p className="text-xs text-[#AEAEB2]">
                                  {formatDate(payment.payment_date)}
                                  {' · '}
                                  {PAYMENT_METHODS[payment.payment_method]?.label || payment.payment_method}
                                </p>
                              </div>
                              <CheckCircle className="w-5 h-5 text-green-500" />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Contact Note */}
                    <p className="text-xs text-[#AEAEB2] text-center">
                      ¿Dudas sobre tu facturación? Escríbenos a hola@paliacatestudio.com
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Upload Receipt Modal */}
      {showUploadModal && (
        <UploadReceiptModal
          payment={showUploadModal}
          onClose={() => setShowUploadModal(null)}
          onSuccess={() => {
            setShowUploadModal(null);
            loadMyPayments();
            toast.success('Tu comprobante fue enviado. Lo revisaremos en breve.');
          }}
        />
      )}
    </div>
  );
}

// Modal para subir comprobante
function UploadReceiptModal({ payment, onClose, onSuccess }) {
  const [file, setFile] = useState(null);
  const [reference, setReference] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      validateAndSetFile(selectedFile);
    }
  };

  const validateAndSetFile = (selectedFile) => {
    if (selectedFile.size > 10 * 1024 * 1024) {
      toast.error('El archivo no debe superar 10 MB');
      return;
    }
    const allowed = ['image/jpeg', 'image/png', 'application/pdf'];
    if (!allowed.includes(selectedFile.type)) {
      toast.error('Solo se aceptan archivos JPG, PNG o PDF');
      return;
    }
    setFile(selectedFile);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile) {
      validateAndSetFile(droppedFile);
    }
  };

  const handleSubmit = async () => {
    if (!file) {
      toast.error('Por favor selecciona un archivo');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('portal_token');
      const formData = new FormData();
      formData.append('file', file);
      if (reference) formData.append('reference', reference);
      if (paymentDate) formData.append('payment_date', paymentDate);

      await axios.post(
        `${API_URL}/api/billing/portal/payments/${payment.payment_id}/upload-receipt`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      onSuccess();
    } catch (error) {
      console.error('Error uploading receipt:', error);
      toast.error(error.response?.data?.detail || 'Error al subir el comprobante');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="upload-receipt-modal">
      <div className="bg-white/65 rounded-2xl max-w-md w-full shadow-2xl border border-[rgba(0,0,0,0.06)]">
        {/* Header */}
        <div className="p-5 border-b border-[rgba(0,0,0,0.06)] flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-[#1D1D1F]">Subir comprobante de pago</h2>
            <p className="text-sm text-[#AEAEB2]">${payment.amount?.toLocaleString()} MXN</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/70 rounded-lg transition">
            <X className="w-5 h-5 text-[#AEAEB2]" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          {/* File Upload */}
          <div>
            <label className="block text-sm font-medium text-[#6E6E73] mb-2">Comprobante</label>
            <div
              onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
              onDragLeave={() => setDragActive(false)}
              onDrop={handleDrop}
              className={`relative border-2 border-dashed rounded-xl p-6 text-center transition cursor-pointer
                ${dragActive ? 'border-orange-500 bg-orange-500/10' : 'border-[rgba(0,0,0,0.08)] hover:border-gray-500 hover:bg-white/50'}
                ${file ? 'border-green-500 bg-green-500/10' : ''}`}
            >
              <input
                type="file"
                accept="image/jpeg,image/png,application/pdf"
                onChange={handleFileChange}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                data-testid="receipt-file-input"
              />
              {file ? (
                <div className="flex items-center justify-center gap-3">
                  <FileText className="w-8 h-8 text-green-400" />
                  <div className="text-left">
                    <p className="text-[#1D1D1F] font-medium">{file.name}</p>
                    <p className="text-xs text-[#AEAEB2]">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                  </div>
                </div>
              ) : (
                <>
                  <Upload className="w-10 h-10 text-[#AEAEB2] mx-auto mb-2" />
                  <p className="text-[#AEAEB2]">Arrastra tu archivo aquí o haz clic para seleccionar</p>
                  <p className="text-xs text-[#AEAEB2] mt-1">JPG, PNG o PDF · Máximo 10 MB</p>
                </>
              )}
            </div>
          </div>

          {/* Reference */}
          <div>
            <label className="block text-sm font-medium text-[#6E6E73] mb-2">
              Referencia o número de transferencia <span className="text-[#AEAEB2]">(opcional)</span>
            </label>
            <input
              type="text"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="Ej: REF123456"
              className="w-full px-4 py-3 bg-white/70 border border-[rgba(0,0,0,0.06)] rounded-xl text-[#1D1D1F] placeholder-[#AEAEB2] focus:outline-none focus:ring-2 focus:ring-orange-500"
              data-testid="receipt-reference-input"
            />
          </div>

          {/* Payment Date */}
          <div>
            <label className="block text-sm font-medium text-[#6E6E73] mb-2">
              Fecha en que realizaste el pago
            </label>
            <input
              type="date"
              value={paymentDate}
              onChange={(e) => setPaymentDate(e.target.value)}
              className="w-full px-4 py-3 bg-white/70 border border-[rgba(0,0,0,0.06)] rounded-xl text-[#1D1D1F] focus:outline-none focus:ring-2 focus:ring-orange-500"
              data-testid="receipt-date-input"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="p-5 border-t border-[rgba(0,0,0,0.06)] flex gap-3">
          <button
            onClick={handleSubmit}
            disabled={loading || !file}
            className="flex-1 bg-gradient-to-r from-orange-500 to-amber-500 text-white py-3 rounded-xl font-bold disabled:opacity-50 flex items-center justify-center gap-2 transition"
            data-testid="submit-receipt-btn"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <Upload className="w-5 h-5" />
                Enviar comprobante
              </>
            )}
          </button>
          <button
            onClick={onClose}
            disabled={loading}
            className="px-6 py-3 bg-white/70 hover:bg-white/70 text-[#1D1D1F] rounded-xl font-semibold transition"
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}
